import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { COLORS, RADIUS } from "../../theme";

// Ícone customizado em vez do padrão do Leaflet — o padrão depende de arquivos
// de imagem que o bundler do CRA não resolve certo por padrão (gotcha
// conhecido da lib), e assim já fica visualmente igual ao pin usado no
// comprovante (MapaLocalizacao.js), sem precisar consertar caminho de asset.
const iconePin = L.divIcon({
  className: "",
  html: `<svg width="30" height="40" viewBox="0 0 30 40" style="filter: drop-shadow(0 2px 3px rgba(0,0,0,0.4))">
    <path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 25 15 25s15-14.5 15-25C30 6.7 23.3 0 15 0z" fill="${COLORS.danger}" />
    <circle cx="15" cy="15" r="6" fill="#fff" />
  </svg>`,
  iconSize: [30, 40],
  iconAnchor: [15, 40],
});

const CENTRO_BRASIL = [-14.235, -51.9253];

// Mapa interativo pra escolher a coordenada de um posto clicando/arrastando
// um marcador — alternativa ao botão "Usar minha localização atual" pra
// quando quem está configurando não está fisicamente no local (ou está usando
// um computador sem GPS confiável). Leaflet puro (sem react-leaflet, que
// ainda não tem suporte maduro pro React 19 desse projeto) montado
// imperativamente num <div> via ref.
export default function MapaEscolhaLocalizacao({ latitude, longitude, onChange, altura = 280 }) {
  const containerRef = useRef(null);
  const mapaRef = useRef(null);
  const marcadorRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!containerRef.current || mapaRef.current) return;

    const temPonto = latitude != null && longitude != null;
    const mapa = L.map(containerRef.current, {
      center: temPonto ? [latitude, longitude] : CENTRO_BRASIL,
      zoom: temPonto ? 17 : 4,
    });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '© colaboradores do <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(mapa);

    if (temPonto) {
      marcadorRef.current = L.marker([latitude, longitude], { icon: iconePin, draggable: true }).addTo(mapa);
      marcadorRef.current.on("dragend", () => {
        const pos = marcadorRef.current.getLatLng();
        onChangeRef.current(pos.lat, pos.lng);
      });
    }

    mapa.on("click", (e) => {
      const { lat, lng } = e.latlng;
      if (marcadorRef.current) {
        marcadorRef.current.setLatLng([lat, lng]);
      } else {
        marcadorRef.current = L.marker([lat, lng], { icon: iconePin, draggable: true }).addTo(mapa);
        marcadorRef.current.on("dragend", () => {
          const pos = marcadorRef.current.getLatLng();
          onChangeRef.current(pos.lat, pos.lng);
        });
      }
      onChangeRef.current(lat, lng);
    });

    mapaRef.current = mapa;

    return () => {
      mapa.remove();
      mapaRef.current = null;
      marcadorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sincroniza o mapa quando lat/long mudam de fora (busca por endereço, botão
  // de GPS) — o Leaflet não é controlado pelo React, então precisa empurrar a
  // mudança manualmente pro mapa já montado.
  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa || latitude == null || longitude == null) return;
    const atual = marcadorRef.current?.getLatLng();
    if (atual && Math.abs(atual.lat - latitude) < 1e-9 && Math.abs(atual.lng - longitude) < 1e-9) return;

    if (marcadorRef.current) {
      marcadorRef.current.setLatLng([latitude, longitude]);
    } else {
      marcadorRef.current = L.marker([latitude, longitude], { icon: iconePin, draggable: true }).addTo(mapa);
      marcadorRef.current.on("dragend", () => {
        const pos = marcadorRef.current.getLatLng();
        onChangeRef.current(pos.lat, pos.lng);
      });
    }
    mapa.setView([latitude, longitude], Math.max(mapa.getZoom(), 16));
  }, [latitude, longitude]);

  return (
    <div>
      <div
        ref={containerRef}
        style={{ height: altura, width: "100%", borderRadius: RADIUS.md, border: `1px solid ${COLORS.border}` }}
      />
      <p style={{ fontSize: 11, color: COLORS.textLight, margin: "6px 0 0" }}>
        Clique no mapa ou arraste o marcador pra ajustar o ponto exato.
      </p>
    </div>
  );
}
